'use client';

import { FC, useId, useState, ReactNode } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { ChatsIcon } from '@/components';
import { COLORS } from '@/constants';
import { CarouselWrapper, SwiperBox, OverlayContent, LeftButtonsGroup, BadgeButton, BadgeText } from './styles';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export type BadgeType = 'new' | 'contacted';

interface CarouselProps {
  images: string[];
  badges?: BadgeType[];
  topRight?: ReactNode;
  className?: string;
}

export const Carousel: FC<CarouselProps> = ({
  images,
  badges,
  topRight,
  className,
}) => {
  const uniqueId = useId();
  const [slides, setSlides] = useState(
    images.map((img, idx) => ({
      src: img,
      loaded: idx === 0,
    }))
  );

  return (
    <CarouselWrapper className={className}>
      {(badges?.length || topRight) && (
        <OverlayContent>
          {badges && badges.length > 0 && (
            <LeftButtonsGroup>
              {badges.includes('new') && (
                <BadgeButton>
                  <BadgeText color={COLORS['green/500']}>New</BadgeText>
                </BadgeButton>
              )}
              {badges.includes('contacted') && (
                <BadgeButton>
                  <ChatsIcon fill={COLORS['primary/500']} width={13} height={12} />
                  <BadgeText color={COLORS['primary/500']}>Kontaktiert</BadgeText>
                </BadgeButton>
              )}
            </LeftButtonsGroup>
          )}
          {topRight && <div>{topRight}</div>}
        </OverlayContent>
      )}
      <SwiperBox>
        <Swiper
          style={{ height: '100%' }}
          modules={[Navigation, Pagination]}
          navigation={{
            nextEl: `.${uniqueId}-next`,
            prevEl: `.${uniqueId}-prev`,
          }}
          pagination={{ el: `.${uniqueId}-custom-pagination`, clickable: true }}
          onSlideChange={(swiper) => {
            const index = swiper.activeIndex;
            setSlides((prev) =>
              prev.map((s, idx) => (idx <= index + 1 ? { ...s, loaded: true } : s))
            );
          }}
        >
          {slides.map((slide, idx) => (
            <SwiperSlide key={idx} style={{ width: '100%' }}>
              {slide.loaded && (
                <img
                  alt={`Image ${idx + 1}`}
                  src={slide.src}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              )}
            </SwiperSlide>
          ))}
        </Swiper>

        {slides.length > 1 && (
          <>
            <button className={`custom-prev ${uniqueId}-prev`} aria-label="Previous slide">
              <span aria-hidden="true">‹</span>
            </button>
            <button className={`custom-next ${uniqueId}-next`} aria-label="Next slide">
              <span aria-hidden="true">›</span>
            </button>
            <div style={{ width: 'auto' }} className={`custom-pagination ${uniqueId}-custom-pagination`}></div>
          </>
        )}
      </SwiperBox>
    </CarouselWrapper>
  );
};

